import { FC, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material"
import { StopCircle, DeleteOutlined, Visibility } from "@mui/icons-material"
import { GridColDef } from "@mui/x-data-grid-pro"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useSnackbar } from "notistack"
import { TooltipAction } from "../common/tooltipAction"
import { DataGrid } from "@/core/components"
import { useUrlListState, useUrlFilters, UrlFilterSchema } from "@/core/hooks"
import { getApiErrorMessage } from "@/core/utils"
import { Userbot } from "../../interfaces"
import {
  useChainsQuery,
  useFinishChainMutation,
  useRemoveChainMutation,
} from "../../store"
import { useUserbotWriteAccess } from "../../hooks"
import { ConfirmDialog } from "../common/confirmDialog"
import { ActiveChainReport } from "./activeChainReport"
import { IdentityResolverCell } from "../identity"

const FILTERS_SCHEMA = {
  status: "string",
} as const satisfies UrlFilterSchema

export const ChainsTable: FC = () => {
  const { read: readFilters } = useUrlFilters(FILTERS_SCHEMA)
  const [, setSearchParams] = useSearchParams()
  const { status: statusFilter } = readFilters()
  const status: Userbot.ChainsQueryParams["status"] =
    statusFilter === "active" || statusFilter === "finished"
      ? statusFilter
      : "all"

  const list = useUrlListState()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const canWrite = useUserbotWriteAccess()

  const { data, isFetching, isError, error, refetch } = useChainsQuery(
    {
      status: status === "all" ? undefined : status,
      limit: list.listArgs.limit,
      offset: list.listArgs.offset,
    },
    { pollingInterval: status === "finished" ? undefined : 60000 },
  )

  const [finishChain, { isLoading: isFinishing }] = useFinishChainMutation()
  const [removeChain, { isLoading: isRemoving }] = useRemoveChainMutation()

  const [reportId, setReportId] = useState<string | null>(null)
  const [toFinish, setToFinish] = useState<Userbot.ChainListItem | null>(null)
  const [toRemove, setToRemove] = useState<Userbot.ChainListItem | null>(null)

  const handleStatusChange = (newStatus: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete("page")
        if (newStatus === "all") {
          next.delete("status")
        } else {
          next.set("status", newStatus)
        }
        return next
      },
      { replace: true },
    )
  }

  const columns: GridColDef<Userbot.ChainListItem>[] = useMemo(
    () => [
      {
        field: "status",
        headerName: "Статус",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) =>
          row.status === "active" ? (
            <Chip size="small" color="info" label="Активна" />
          ) : (
            <Chip size="small" color="default" label="Завершена" />
          ),
      },
      {
        field: "name",
        headerName: "Цепочка",
        flex: 3,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack spacing={0} sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={500} noWrap>
              {row.name}
            </Typography>
            {row.comment ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.comment}
              </Typography>
            ) : null}
          </Stack>
        ),
      },
      {
        field: "clientId",
        headerName: "Клиент",
        flex: 2,
        sortable: false,
        renderCell: ({ row }) => (
          <IdentityResolverCell clientId={row.clientId} />
        ),
      },
      {
        field: "progress",
        headerName: "Прогресс",
        flex: 2,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            <Typography component="span" color="success.main" fontWeight={600}>
              ✔{row.completed}
            </Typography>{" "}
            <Typography component="span" color="error.main" fontWeight={600}>
              ✘{row.failed}
            </Typography>{" "}
            <Typography component="span" color="warning.main" fontWeight={600}>
              ⊘{row.skipped}
            </Typography>
          </Typography>
        ),
      },
      {
        field: "createdAt",
        headerName: "Создана",
        flex: 2,
        sortable: false,
        valueGetter: ({ row }) =>
          row.createdAt ? new Date(row.createdAt).toLocaleString("ru") : "—",
      },
      {
        field: "finishedAt",
        headerName: "Завершена",
        flex: 2,
        sortable: false,
        valueGetter: ({ row }) =>
          row.status === "finished" && row.finishedAt
            ? new Date(row.finishedAt).toLocaleString("ru")
            : "—",
      },
    ],
    [],
  )

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Статус:
          </Typography>
          <Select
            size="small"
            value={status}
            onChange={(e) => handleStatusChange(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">Все</MenuItem>
            <MenuItem value="active">Активные</MenuItem>
            <MenuItem value="finished">Завершённые</MenuItem>
          </Select>
        </Stack>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {getApiErrorMessage(error)}
        </Alert>
      )}

      {isFetching && <LinearProgress sx={{ mb: 1 }} />}

      <Box height="78vh">
        <DataGrid<Userbot.ChainListItem>
          columns={columns}
          rows={data?.items ?? []}
          getRowId={(row) => row.id}
          loading={isFetching && !data}
          rowCount={data?.total ?? 0}
          filterMode="client"
          {...list.gridProps}
          sortingMode="client"
          rowActions={[
            (r) => (
              <TooltipAction
                label="Детализация"
                icon={<Visibility />}
                onClick={() => {
                  if (r.row.status === "active") {
                    setReportId(r.row.id)
                  } else {
                    navigate(`/userbot/chains/finished/${r.row.id}`)
                  }
                }}
              />
            ),
            (r) => {
              if (r.row.status !== "active" || !canWrite) return <></>
              return (
                <TooltipAction
                  label="Финализировать"
                  icon={<StopCircle />}
                  onClick={() => setToFinish(r.row)}
                />
              )
            },
            (r) => {
              if (r.row.status !== "active" || !canWrite) return <></>
              return (
                <TooltipAction
                  label="Удалить данные (не отмена)"
                  icon={<DeleteOutlined />}
                  onClick={() => setToRemove(r.row)}
                />
              )
            },
          ]}
        />
      </Box>

      {reportId && (
        <ActiveChainReport
          chainId={reportId}
          onClose={() => setReportId(null)}
        />
      )}

      <ConfirmDialog
        open={!!toFinish}
        title="Финализировать цепочку?"
        description={
          toFinish
            ? `Финализация «${toFinish.name}» сохраняет итоговый отчёт только в неактивной безопасной точке. Она не прерывает RPC. При конфликте дождитесь безопасной точки; история сохраняется.`
            : ""
        }
        confirmText="Финализировать"
        loading={isFinishing}
        onCancel={() => setToFinish(null)}
        onConfirm={async () => {
          if (toFinish) {
            try {
              await finishChain({ id: toFinish.id }).unwrap()
              setToFinish(null)
            } catch (err) {
              if ((err as { status?: number }).status === 409) {
                refetch()
                enqueueSnackbar(
                  "Финализация пока недоступна: дождитесь неактивной безопасной точки. История и восстановление сохранены.",
                  { variant: "warning" },
                )
              } else
                enqueueSnackbar(getApiErrorMessage(err), { variant: "error" })
            }
          }
        }}
      />

      <ConfirmDialog
        open={!!toRemove}
        title="Удалить цепочку?"
        description={
          toRemove
            ? `Удаление данных «${toRemove.name}» не является отменой. Для восстанавливаемых доставок оно запрещено до сохранённого терминального отчёта. Используйте финализацию в неактивной безопасной точке, затем очистку.`
            : ""
        }
        loading={isRemoving}
        onCancel={() => setToRemove(null)}
        onConfirm={async () => {
          if (toRemove) {
            try {
              await removeChain({ id: toRemove.id }).unwrap()
              setToRemove(null)
            } catch (err) {
              if ((err as { status?: number }).status === 409) {
                refetch()
                enqueueSnackbar(
                  "Удаление запрещено до итогового отчёта. История и восстановление сохранены.",
                  { variant: "warning" },
                )
              } else
                enqueueSnackbar(getApiErrorMessage(err), { variant: "error" })
            }
          }
        }}
      />
    </>
  )
}
